/* eslint-disable max-len */
import { ReactElement, useEffect } from 'react';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../../redux/hooks';
import {
  clearBoardMembers,
  saveBoardMembers,
  selectLoadingBoardMember,
  selectBoardMembersDataEntryMode,
  selectBoardMember,
  selectBoardMembersFormViewModel,
  setBoardMembersDataEntryMode,
  selectSchoolYear,
  selectStateOptions,
} from '../../../../redux/slices/boardMembersSlice';
import {
  fetchBoardMemberById,
  fetchBoardMembersFormViewModel,
  fetchStateOptions
} from '../../../../redux/thunks/boardMembersThunks';
import {
  createBoardMember,
  updateBoardMembers,
} from '../../../../services/boardMembers.service';
import { appRoutePaths } from '../../../../services/route.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { BoardMembersFormViewModel } from '../../../../types/BoardMembersFormViewModel';
import { BoardMembersItemModel } from '../../../../types/BoardMembersItemModel';
import { Tabs_DISTRICT_SCHOOL } from '../../../../types/NavigationTabs';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { BoardMembersValidationEditSchema } from '../../../../types/Validation/BoardMembersValidationEditSchema';
import { BoardMembersValidationSchema } from '../../../../types/Validation/BoardMembersValidationSchema';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import './BoardMembersInput.css';
import BoardMembers_Layout from 'src/components/FormLayouts/BoardMembers/BoardMembers_Layout';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import { useAppDispatch } from '../../../../redux/hooks';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { isAdmin } from 'src/utilities/userUtilities';

const BoardMembersInput = (): ReactElement => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const allowEdit = location.state?.allowEdit && isAdmin();

  const boardMembersModel: BoardMembersItemModel = useAppSelector(
    selectBoardMember
  );
  const formViewModel: BoardMembersFormViewModel = useAppSelector(
    selectBoardMembersFormViewModel
  );
	const isLoadingFormViewModel = false;

  const isLoadingBoardMembersModel = useAppSelector(
    selectLoadingBoardMember
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectBoardMembersDataEntryMode
  );
  const stateOptions = useAppSelector(selectStateOptions);
  const selectedSchoolYearRange = useAppSelector(selectSchoolYear);
  const isLoading = isLoadingBoardMembersModel || isLoadingFormViewModel;
  const supportedNonGenericErrors = [
    {
      statusCode: 400,
    },
    {
      statusCode: 409,
      howToFix:
        'The provided Board Members ID is already in the system. Use another value, or return to the list and edit the existing Board Members.',
    },
  ];
const selectedDistrict = useAppSelector(selectSelectedDistrict);
  useEffect(() => {
    const promise = dispatch(fetchStateOptions());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const initialValues: BoardMembersItemModel = {
    ...boardMembersModel,
    leaIdentifier: dataEntryMode === DataEntryMode.NEW
                   ? selectedDistrict.districtId // Use district ID for 'new' mode
                   : boardMembersModel.leaIdentifier,
    schoolYear: dataEntryMode === DataEntryMode.NEW && selectedSchoolYearRange
                  ? selectedSchoolYearRange.value // Extract ending year for NEW mode
                  : boardMembersModel.schoolYear,
  };


  return (
    <ManualInputPage<BoardMembersItemModel, BoardMembersFormViewModel>
      actions={ManualInputCrudAction.CREATE_AND_EDIT}
      loadingOptions={{
        isLoading: isLoading,
        loadingDataId: 'board-members-input-loader',
      }}
      navigationTabs={buildUserAccessNavigationTabItems(Tabs_DISTRICT_SCHOOL)}
      classes={{ pageClass: 'data-input--board-members--input' }}
      parameterOptions={{
        routeParameterName: 'id',
        identityName: boardMembersModel.id,
      }}
      routes={{
        backBarRoute: appRoutePaths.BoardMembersList,
        cancelRoute: appRoutePaths.BoardMembersList,
        createSuccessRoute: appRoutePaths.BoardMembersInput,
      }}
      dataFunctions={{
        loadDataDetailById: fetchBoardMemberById,
        loadFormDetail: fetchBoardMembersFormViewModel,
        setEntryMode: setBoardMembersDataEntryMode,
        setData: saveBoardMembers,
        clearData: clearBoardMembers,
        onCreate: createBoardMember,
        onUpdate: updateBoardMembers,
      }}
      formDetails={{
        validationSchema:
          dataEntryMode === 'new'
            ? BoardMembersValidationSchema
            : BoardMembersValidationEditSchema,
        initialFormValues: initialValues,
        isFormModelEmpty: !formViewModel.membershipType?.length,
      }}
      config={{
        dataEntryMode: dataEntryMode,
        objectVerbiage: 'Board Member',
        pageHeader: 'LEA & School Info',
        supportedNonGenericErrors: supportedNonGenericErrors,
      }}
      DEV_MODE={GLOBAL_DEBUG_MODE}
    >
      <div data-testid="board-members-input-child">
        <BoardMembers_Layout 
          dataEntryMode={dataEntryMode}
          formViewModel={formViewModel}
          allowEdit={allowEdit}
          stateOptions={stateOptions}
        />
      </div>
    </ManualInputPage>
  );
};

export default BoardMembersInput;
